import clsx from 'clsx';
import * as React from 'react';

export type GroupProps = React.ComponentPropsWithoutRef<'div'> & {
    orientation?: 'horizontal' | 'vertical';
};

/**
 * Used to display elements in a group with automatic gap.
 * @example
 * <Group>
 *   <Heading>Title</Heading>
 *   <Description>Some description</Description>
 * </Group>
 */
export function Group({
    ref,
    ...props
}: GroupProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const { orientation, ...rest } = props;
    return (
        <div
            ref={ref}
            role="group"
            {...rest}
            className={clsx(
                'group/group peer/group',
                'flex gap-[inherit]',
                orientation !== 'horizontal' && [
                    // With descriptor (label or heading) + description, render horizontal with a gap of 1
                    String.raw`has-[>.peer\/descriptor+.peer\/description]:flex-col`,
                    String.raw`has-[>.peer\/descriptor+.peer\/description]:items-start`,
                    String.raw`has-[>.peer\/descriptor+.peer\/description]:gap-1`,
                    // With group + description, render horizontal with a gap of 1
                    String.raw`has-[>.peer\/group+.peer\/description]:flex-col`,
                    String.raw`has-[>.peer\/group+.peer\/description]:items-start`,
                    String.raw`has-[>.peer\/group+.peer\/description]:gap-1`,
                ],
                // With headings and label
                'has-[>.text-ui-heading-large]:gap-4',
                'has-[>.text-ui-heading-medium]:gap-3',
                'has-[>.text-ui-heading-small]:gap-2',
                { vertical: 'flex-col items-start', horizontal: 'items-center' }[
                    orientation ?? 'horizontal'
                ],
                rest.className
            )}
        />
    );
}
